import { useState } from 'react';
import { motion } from 'framer-motion';
import { ChevronDown, Shield, Circle, CreditCard, Car, ShieldCheck, Wrench } from 'lucide-react';
import { useLanguage } from '@/context/LanguageContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Helper to get image URL
const getImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http')) return url;
  return `${API_URL}${url}`;
};

// Icon mapping for services
const iconMap = {
  'shield-check': ShieldCheck,
  'shield': Shield,
  'circle': Circle,
  'credit-card': CreditCard,
  'car': Car,
  'wrench': Wrench,
};

// FAQ Section Component
export const FAQSection = ({ settings }) => {
  const { language } = useLanguage();
  const [openIndex, setOpenIndex] = useState(null);
  
  const s = settings || {};
  const title = language === 'en' ? (s.faq_title_en || 'FAQ') : (s.faq_title || 'Häufige Fragen');
  const subtitle = language === 'en' ? (s.faq_subtitle_en || '') : (s.faq_subtitle || '');
  const items = s.faq_items || [];
  
  // Check if explicitly disabled (default is enabled)
  if (s.module_faq_enabled === false) return null;
  if (items.length === 0) return null;

  return (
    <section className="py-20 bg-theme-surface" id="faq" data-testid="faq-section">
      <div className="max-w-[1600px] mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <p className="overline text-primary mb-2">{subtitle}</p>
          <h2 className="font-heading text-3xl md:text-4xl text-theme">{title}</h2>
        </motion.div>

        <div className="max-w-3xl mx-auto space-y-4">
          {items.map((item, index) => {
            const question = language === 'en' ? (item.question_en || item.question) : item.question;
            const answer = language === 'en' ? (item.answer_en || item.answer) : item.answer;
            const isOpen = openIndex === index;
            
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="border border-theme rounded-lg overflow-hidden"
              >
                <button
                  onClick={() => setOpenIndex(isOpen ? null : index)}
                  className="w-full flex items-center justify-between p-5 text-left bg-theme-surface hover:bg-theme-surface/80 transition-colors"
                  data-testid={`faq-question-${index}`}
                >
                  <span className="font-medium text-theme pr-4">{question}</span>
                  <ChevronDown 
                    size={20} 
                    className={`text-primary flex-shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`} 
                  />
                </button>
                {isOpen && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    className="px-5 pb-5"
                  >
                    <p className="text-theme-muted">{answer}</p>
                  </motion.div>
                )}
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

// Brands/Partners Infinite Scroll Component
export const BrandsSection = ({ settings }) => {
  const { language } = useLanguage();
  const { darkMode } = require('@/context/ThemeContext').useTheme();
  
  const s = settings || {};
  const title = language === 'en' ? (s.brands_title_en || 'Our Brands') : (s.brands_title || 'Unsere Marken');
  const subtitle = language === 'en' ? (s.brands_subtitle_en || '') : (s.brands_subtitle || '');
  const logos = s.brands_logos || [];
  const scrollSpeed = s.brands_scroll_speed || 30;
  
  // Check if explicitly disabled (default is enabled)
  if (s.module_brands_enabled === false) return null;
  if (logos.length === 0) return null;

  // Duplicate logos for infinite scroll effect
  const duplicatedLogos = [...logos, ...logos, ...logos];

  // Get the appropriate logo URL based on dark mode
  const getLogoUrl = (logo) => {
    if (darkMode && logo.url_dark) {
      return logo.url_dark;
    }
    return logo.url;
  };

  return (
    <section className="py-16 bg-theme overflow-hidden" id="brands" data-testid="brands-section">
      <div className="max-w-[1600px] mx-auto px-6 md:px-12 mb-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center"
        >
          <p className="overline text-primary mb-2">{subtitle}</p>
          <h2 className="font-heading text-3xl md:text-4xl text-theme">{title}</h2>
        </motion.div>
      </div>

      {/* Infinite scroll container */}
      <div className="relative">
        <motion.div
          className="flex gap-16 items-center"
          animate={{
            x: [0, -50 * logos.length * 3],
          }}
          transition={{
            x: {
              repeat: Infinity,
              repeatType: "loop",
              duration: scrollSpeed,
              ease: "linear",
            },
          }}
        >
          {duplicatedLogos.map((logo, index) => (
            <div 
              key={index} 
              className="flex-shrink-0 h-16 w-32 flex items-center justify-center"
            >
              {(logo.url || logo.url_dark) && (
                <img
                  src={getImageUrl(getLogoUrl(logo))}
                  alt={logo.name || `Brand ${index + 1}`}
                  className="max-h-full max-w-full object-contain opacity-70 hover:opacity-100 transition-opacity"
                />
              )}
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

// Services Section Component
export const ServicesSection = ({ settings }) => {
  const { language } = useLanguage();
  const { darkMode } = require('@/context/ThemeContext').useTheme();
  
  const s = settings || {};
  const title = language === 'en' ? (s.services_title_en || 'Our Services') : (s.services_title || 'Unsere Services');
  const subtitle = language === 'en' ? (s.services_subtitle_en || '') : (s.services_subtitle || '');
  const items = s.services_items || [];
  
  // Check if explicitly disabled (default is enabled)
  if (s.module_services_enabled === false) return null;
  if (items.length === 0) return null;

  return (
    <section className="py-20 bg-theme-surface" id="services" data-testid="services-section">
      <div className="max-w-[1600px] mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <p className="overline text-primary mb-2">{subtitle}</p>
          <h2 className="font-heading text-3xl md:text-4xl text-theme">{title}</h2>
        </motion.div>

        <div className="flex flex-wrap justify-center gap-6">
          {items.map((item, index) => {
            const itemTitle = language === 'en' ? (item.title_en || item.title) : item.title;
            const description = language === 'en' ? (item.description_en || item.description) : item.description;
            const IconComponent = iconMap[item.icon] || Shield;
            const iconUrl = darkMode ? (item.icon_url_dark || item.icon_url) : item.icon_url;
            
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="group p-6 bg-theme rounded-xl border border-theme hover:border-gold transition-all text-center w-full sm:w-[calc(50%-12px)] md:w-[calc(33.333%-16px)] lg:w-[calc(20%-19.2px)] max-w-[320px]"
                data-testid={`service-item-${index}`}
              >
                <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-gold/10 flex items-center justify-center group-hover:bg-gold/20 transition-colors">
                  {iconUrl ? (
                    <img 
                      src={getImageUrl(iconUrl)} 
                      alt={itemTitle} 
                      className="w-7 h-7 object-contain"
                    />
                  ) : (
                    <IconComponent size={28} className="text-primary" />
                  )}
                </div>
                <h3 className="font-heading text-lg text-theme mb-2">{itemTitle}</h3>
                <p className="text-sm text-theme-muted">{description}</p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

// Team Section Component
export const TeamSection = ({ settings }) => {
  const { language } = useLanguage();
  const s = settings || {};
  
  // Check if homepage display is enabled
  if (s.module_team_homepage_enabled === false) return null;
  
  const teamMembers = s.team_members || [];
  if (teamMembers.length === 0) return null;
  
  const title = language === 'en' ? (s.team_title_en || 'Our Team') : (s.team_title || 'Unser Team');
  const subtitle = language === 'en' ? (s.team_subtitle_en || 'Meet the experts') : (s.team_subtitle || 'Lernen Sie unsere Experten kennen');

  return (
    <section className="py-20 bg-theme" id="team" data-testid="team-section">
      <div className="max-w-[1600px] mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <p className="overline text-primary mb-2">{subtitle}</p>
          <h2 className="font-heading text-3xl md:text-4xl text-theme">{title}</h2>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {teamMembers.slice(0, 4).map((member, index) => {
            const position = language === 'en' ? (member.position_en || member.position) : member.position;
            
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="group text-center"
                data-testid={`team-member-${index}`}
              >
                <div className="relative mb-4 overflow-hidden rounded-xl aspect-square bg-theme-muted">
                  {member.image_url ? (
                    <img 
                      src={getImageUrl(member.image_url)} 
                      alt={member.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-gold/10 to-gold/5">
                      <span className="text-5xl font-heading text-primary/30">
                        {member.name?.charAt(0) || '?'}
                      </span>
                    </div>
                  )}
                </div>
                <h3 className="font-heading text-lg text-theme">{member.name}</h3>
                <p className="text-sm text-theme-muted">{position}</p>
              </motion.div>
            );
          })}
        </div>

        {teamMembers.length > 4 && (
          <div className="text-center mt-8">
            <a 
              href="/team" 
              className="inline-flex items-center gap-2 text-primary hover:underline"
            >
              {language === 'en' ? 'View all team members' : 'Alle Teammitglieder anzeigen'}
              <ChevronDown size={16} className="rotate-[-90deg]" />
            </a>
          </div>
        )}
      </div>
    </section>
  );
};

// Blog Section Component
export const BlogSection = ({ settings, posts }) => {
  const { language } = useLanguage();
  const s = settings || {};
  
  // Check if homepage display is enabled
  if (s.module_blog_homepage_enabled === false) return null;
  
  const blogPosts = posts || [];
  if (blogPosts.length === 0) return null;
  
  const title = language === 'en' ? (s.blog_title_en || 'Latest News') : (s.blog_title || 'Neuigkeiten');
  const subtitle = language === 'en' ? (s.blog_subtitle_en || 'Stay updated') : (s.blog_subtitle || 'Bleiben Sie informiert');

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString(language === 'en' ? 'en-US' : 'de-DE', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <section className="py-20 bg-theme-surface" id="blog" data-testid="blog-section">
      <div className="max-w-[1600px] mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <p className="overline text-primary mb-2">{subtitle}</p>
          <h2 className="font-heading text-3xl md:text-4xl text-theme">{title}</h2>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {blogPosts.slice(0, 3).map((post, index) => {
            const postTitle = language === 'en' ? (post.title_en || post.title) : post.title;
            const excerpt = language === 'en' ? (post.excerpt_en || post.excerpt) : post.excerpt;
            
            return (
              <motion.a
                key={post.id}
                href={`/blog/${post.id}`}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="group block"
                data-testid={`blog-post-${index}`}
              >
                <div className="relative mb-4 overflow-hidden rounded-xl aspect-video bg-theme-muted">
                  {post.image_url ? (
                    <img 
                      src={getImageUrl(post.image_url)} 
                      alt={postTitle}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-gold/10 to-gold/5">
                      <span className="text-4xl text-primary/30">📰</span>
                    </div>
                  )}
                </div>
                <p className="text-xs text-theme-muted mb-2">{formatDate(post.created_at)}</p>
                <h3 className="font-heading text-lg text-theme group-hover:text-primary transition-colors mb-2">
                  {postTitle}
                </h3>
                {excerpt && (
                  <p className="text-sm text-theme-muted line-clamp-2">{excerpt}</p>
                )}
              </motion.a>
            );
          })}
        </div>

        <div className="text-center mt-8">
          <a 
            href="/blog" 
            className="inline-flex items-center gap-2 text-primary hover:underline"
          >
            {language === 'en' ? 'View all posts' : 'Alle Beiträge anzeigen'}
            <ChevronDown size={16} className="rotate-[-90deg]" />
          </a>
        </div>
      </div>
    </section>
  );
};
